import type {
  CellId,
  GridSudokuDefinition,
  GridSudokuProgress,
  GridSudokuSubmission,
  Shade,
} from "@/lib/puzzle-format/types";
import { rectCellId } from "@/lib/puzzle-format/types";

/** The solution layer the player's shading is submitted as (docs/constraint-catalogue.md §4). */
export const SHADING_LAYER = "coloring";

const SHADES: readonly Shade[] = ["shaded", "unshaded"];

export function isShade(value: unknown): value is Shade {
  return typeof value === "string" && (SHADES as readonly string[]).includes(value);
}

/** Constraint types that read the shading layer. */
export const SHADING_TYPES: readonly string[] = ["yin_yang", "shade_count"];

/** Whether the puzzle asks the solver to shade cells: a declared layer or a rule reading it. */
export function hasShading(definition: GridSudokuDefinition): boolean {
  if (definition.solutionShape?.[SHADING_LAYER]?.required) return true;
  return [...definition.constraints, ...definition.globalConstraints].some((instance) =>
    SHADING_TYPES.includes(instance.type),
  );
}

/** The solution layer that assigns each cell to a region (chaos construction, ADR 0071). */
export const REGIONS_LAYER = "regions";

/** Constraint types that read the regions layer. */
export const REGION_TYPES: readonly string[] = ["chaos_construction"];

/** Whether the solver draws the regions: a declared layer or a rule reading it. */
export function hasRegionDrawing(definition: GridSudokuDefinition): boolean {
  if (definition.solutionShape?.[REGIONS_LAYER]?.required) return true;
  return [...definition.constraints, ...definition.globalConstraints].some((instance) =>
    REGION_TYPES.includes(instance.type),
  );
}

/** The solution layer of cell modifiers (doublers, ADR 0072): sparse, `double` where marked. */
export const MODIFIERS_LAYER = "modifiers";
export const DOUBLE = "double";

/** Constraint types that read the modifiers layer. */
export const MODIFIER_TYPES: readonly string[] = ["doublers"];

/** Whether the solver marks doublers: a declared layer or a rule reading it. */
export function hasModifiers(definition: GridSudokuDefinition): boolean {
  if (definition.solutionShape?.[MODIFIERS_LAYER]) return true;
  return [...definition.constraints, ...definition.globalConstraints].some((instance) =>
    MODIFIER_TYPES.includes(instance.type),
  );
}

/** Modifiers kept from a stored blob: only `double`, the one the player can set. */
export function modifierMap(value: unknown): Record<CellId, string> {
  const out: Record<CellId, string> = {};
  for (const [k, v] of Object.entries(labelMap(value))) if (v === DOUBLE) out[k] = v;
  return out;
}

/** The second digit of each Schrödinger cell (ADR 0073): sparse, a digit string where set. */
export const SUPERPOSITION_LAYER = "schrodinger";

/** Constraint types that read the superposition layer. */
export const SUPERPOSITION_TYPES: readonly string[] = ["schrodinger_cells"];

/** Whether some cells hold two digits: a declared layer or a rule reading it. */
export function hasSuperposition(definition: GridSudokuDefinition): boolean {
  if (definition.solutionShape?.[SUPERPOSITION_LAYER]) return true;
  return [...definition.constraints, ...definition.globalConstraints].some((instance) =>
    SUPERPOSITION_TYPES.includes(instance.type),
  );
}

export function emptyDefinition(): GridSudokuDefinition {
  return {
    puzzleType: "grid_sudoku",
    typeVersion: 1,
    topology: { kind: "rect", rows: 9, cols: 9, digits: { min: 1, max: 9 } },
    solutionShape: { digits: { required: true } },
    givens: [],
    variables: [],
    globalConstraints: [{ type: "latin_rows" }, { type: "latin_cols" }, { type: "latin_regions" }],
    constraints: [],
    cosmetics: [],
    rulesText: { fr: "", en: "" },
    checkStrategy: "either",
  };
}

export function emptyProgress(_definition: GridSudokuDefinition): GridSudokuProgress {
  return {
    version: 1,
    digits: {},
    centerMarks: {},
    cornerMarks: {},
    colors: {},
    shading: {},
    regions: {},
    modifiers: {},
    superposition: {},
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function numberMap(value: unknown): Record<CellId, number> {
  const out: Record<CellId, number> = {};
  if (!isRecord(value)) return out;
  for (const [k, v] of Object.entries(value)) {
    if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
    else if (typeof v === "string" && /^\d+$/.test(v)) out[k] = Number(v);
  }
  return out;
}

export function shadeMap(value: unknown): Record<CellId, Shade> {
  const out: Record<CellId, Shade> = {};
  if (!isRecord(value)) return out;
  for (const [k, v] of Object.entries(value)) if (isShade(v)) out[k] = v;
  return out;
}

/** Region labels: non-empty strings, numbers read as their decimal form. */
export function labelMap(value: unknown): Record<CellId, string> {
  const out: Record<CellId, string> = {};
  if (!isRecord(value)) return out;
  for (const [k, v] of Object.entries(value)) {
    if (typeof v === "string" && v !== "") out[k] = v;
    else if (typeof v === "number" && Number.isFinite(v)) out[k] = String(v);
  }
  return out;
}

function listMap(value: unknown): Record<CellId, number[]> {
  const out: Record<CellId, number[]> = {};
  if (!isRecord(value)) return out;
  for (const [k, v] of Object.entries(value)) {
    if (Array.isArray(v)) out[k] = v.filter((x): x is number => typeof x === "number");
  }
  return out;
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(
  definition: GridSudokuDefinition,
  raw: unknown,
): GridSudokuProgress {
  const base = emptyProgress(definition);
  if (!isRecord(raw)) return base;
  return {
    version: 1,
    digits: numberMap(raw.digits),
    centerMarks: listMap(raw.centerMarks),
    cornerMarks: listMap(raw.cornerMarks),
    colors: listMap(raw.colors),
    shading: shadeMap(raw.shading),
    regions: labelMap(raw.regions),
    modifiers: modifierMap(raw.modifiers),
    superposition: numberMap(raw.superposition),
  };
}

export function givensOf(definition: GridSudokuDefinition): Record<CellId, number> {
  const out: Record<CellId, number> = {};
  for (const g of definition.givens ?? []) {
    if (definition.topology.kind === "rect") out[rectCellId(g.r, g.c)] = g.v;
    else {
      const cell = definition.topology.cells.find((c) => c.row === g.r && c.col === g.c);
      if (cell) out[cell.id] = g.v;
    }
  }
  return out;
}

/**
 * Digits layer = progress digits merged with the givens (givens win). The shading becomes the
 * `coloring` layer whenever the puzzle asks for one, or the player shaded something anyway; the
 * drawn regions become the `regions` layer the same way.
 */
export function serializeSubmission(
  definition: GridSudokuDefinition,
  progress: GridSudokuProgress,
): GridSudokuSubmission {
  const shading = progress.shading ?? {};
  const layers: GridSudokuSubmission["layers"] = {
    digits: { ...progress.digits, ...givensOf(definition) },
  };
  if (hasShading(definition) || Object.keys(shading).length > 0) {
    layers[SHADING_LAYER] = { ...shading };
  }
  const regions = progress.regions ?? {};
  if (hasRegionDrawing(definition) || Object.keys(regions).length > 0) {
    layers[REGIONS_LAYER] = { ...regions };
  }
  const modifiers = progress.modifiers ?? {};
  if (hasModifiers(definition) || Object.keys(modifiers).length > 0) {
    layers[MODIFIERS_LAYER] = { ...modifiers };
  }
  const superposition = progress.superposition ?? {};
  if (hasSuperposition(definition) || Object.keys(superposition).length > 0) {
    layers[SUPERPOSITION_LAYER] = Object.fromEntries(
      Object.entries(superposition).map(([cell, digit]) => [cell, String(digit)]),
    );
  }
  return { layers };
}
